import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// useGroupCatering — group-scoped read hook for the combined catering
// pipeline (Ship 4). The group analogue of useCateringData: it loads
// EVERY catering inquiry across the group (via the new group-owner SELECT
// RLS policy from migration 0053) plus the group's units, and exposes the
// same status grouping the single-unit screen uses.
//
// THE ZERO-BEHAVIOR-CHANGE CONTRACT: this hook is ONLY used by the
// group-level pipeline (GroupCateringPipeline). useCateringData — the
// single-unit hook — is left completely untouched, so single-unit
// accounts see no change.
//
// Reads:
//   - catering_inquiries WHERE group_id = <gid>  (RLS via group-owner SELECT)
//   - restaurants WHERE group_id = <gid> AND active  (id, name) → units map
//
// Mutations:
//   - updateAssignment(id, assigned_restaurant_id) — thin reassign
//   - createInquiry(payload) — insert with assigned_restaurant_id + group_id
export function useGroupCatering(groupId) {
  // --- All hooks declared BEFORE any conditional return (React #310). ---
  const [inquiries, setInquiries] = useState([])
  const [units, setUnits]         = useState([]) // [{id,name}]
  const [loading, setLoading]     = useState(true)
  const [error, setError]         = useState(null)

  const fetchAll = useCallback(async () => {
    if (!groupId) {
      setInquiries([])
      setUnits([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [inqRes, unitRes] = await Promise.all([
        supabase
          .from('catering_inquiries')
          .select('*')
          .eq('group_id', groupId)
          .order('event_date', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false })
          .limit(500),
        supabase
          .from('restaurants')
          .select('id, name')
          .eq('group_id', groupId)
          .eq('active', true)
          .order('name'),
      ])
      if (inqRes.error) throw inqRes.error
      if (unitRes.error) throw unitRes.error
      setInquiries(inqRes.data ?? [])
      setUnits(unitRes.data ?? [])
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useGroupCatering]', e)
    } finally {
      setLoading(false)
    }
  }, [groupId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Same midnight-rollover + visibilitychange refresh pattern as every
  // other data hook in the project.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') fetchAll()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  // id → name lookup for the assigned-unit chip.
  const unitsById = useMemo(() => {
    const m = new Map()
    for (const u of units) m.set(u.id, u)
    return m
  }, [units])

  // Status grouping — identical buckets to CateringScreen's byStatus.
  const byStatus = useMemo(() => {
    const groups = {
      new:        [],
      quoted:     [],
      confirmed:  [],
      completed:  [],
      cancelled:  [],
      declined:   [],
    }
    for (const i of inquiries) {
      if (groups[i.status]) groups[i.status].push(i)
    }
    return groups
  }, [inquiries])

  // ---------- Mutations -------------------------------------
  // Thin reassign — flips assigned_restaurant_id only. Goes through the
  // group-owner UPDATE policy (migration 0053). Re-fetches on success.
  const updateAssignment = useCallback(async (id, assignedRestaurantId) => {
    const { data, error: updErr } = await supabase
      .from('catering_inquiries')
      .update({
        assigned_restaurant_id: assignedRestaurantId || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()
    if (updErr) throw updErr
    await fetchAll()
    return data
  }, [fetchAll])

  // Generic edit/status update — mirrors useCateringData.updateInquiry's
  // forward-only *_at stamping so the group edit modal behaves identically
  // to the single-unit one. Goes through the group-owner UPDATE policy.
  const updateInquiry = useCallback(async (id, patch) => {
    const updates = { ...patch, updated_at: new Date().toISOString() }
    const currentRow = inquiries.find((r) => r.id === id)
    const currentStatus = currentRow?.status
    const nowIso = new Date().toISOString()
    if (patch.status === 'quoted' && currentStatus !== 'quoted') {
      updates.quoted_at = updates.quoted_at ?? nowIso
    }
    if (patch.status === 'confirmed' && currentStatus !== 'confirmed') {
      updates.confirmed_at = updates.confirmed_at ?? nowIso
    }
    if (patch.status === 'completed' && currentStatus !== 'completed') {
      updates.completed_at = updates.completed_at ?? nowIso
    }
    const { data, error: updErr } = await supabase
      .from('catering_inquiries')
      .update(updates)
      .eq('id', id)
      .select()
      .single()
    if (updErr) throw updErr
    await fetchAll()
    return data
  }, [fetchAll, inquiries])

  // Insert a new inquiry from the group pipeline. Persists the operator's
  // (possibly AI-suggested, possibly overridden) assigned_restaurant_id
  // PLUS group_id so the row is visible under the group-owner SELECT
  // policy immediately. Empty strings coerce to null (matches
  // useCateringData.createInquiry).
  //
  // restaurant_id is set EXPLICITLY (the assigned unit, falling back to
  // the first group unit). Two reasons:
  //   1. The default_restaurant_id_single_tenant() BEFORE-INSERT trigger
  //      RAISES when restaurant_id is NULL and >1 active restaurant
  //      exists — which is always true for a grouped owner. We must
  //      supply it.
  //   2. INSERT authorization flows through the existing tenant_access
  //      policy (the approved 0053 policy set is SELECT + UPDATE only),
  //      whose WITH CHECK requires the caller to be a restaurant_members
  //      member of restaurant_id. The group owner is a member of their
  //      units, so the assigned unit satisfies it.
  const createInquiry = useCallback(async (payload) => {
    const clean = (v) => (typeof v === 'string' && v.trim() === '' ? null : v)
    const assignedId = clean(payload.assigned_restaurant_id)
    const restaurantId = assignedId || (units[0]?.id ?? null)
    const insert = {
      restaurant_id:          restaurantId,
      contact_name:           payload.contact_name?.trim(),
      contact_email:          clean(payload.contact_email),
      contact_phone:          clean(payload.contact_phone),
      event_date:             clean(payload.event_date),
      event_time:             clean(payload.event_time),
      headcount:              payload.headcount ? Number(payload.headcount) : null,
      location:               clean(payload.location),
      event_type:             clean(payload.event_type),
      notes:                  clean(payload.notes),
      estimated_total:        payload.estimated_total ? Number(payload.estimated_total) : null,
      status:                 payload.status || 'new',
      assigned_restaurant_id: assignedId || restaurantId,
      group_id:               groupId,
    }
    const { data, error: insErr } = await supabase
      .from('catering_inquiries')
      .insert(insert)
      .select()
      .single()
    if (insErr) throw insErr
    await fetchAll()
    return data
  }, [fetchAll, groupId, units])

  return {
    inquiries,
    byStatus,
    units,
    unitsById,
    loading,
    error,
    refresh: fetchAll,
    updateAssignment,
    updateInquiry,
    createInquiry,
  }
}
